import Seo from '@/components/Seo'

interface LegalDoc {
  path: string
  title: string
  metaTitle: string
  description: string
  updated: string
  sections: { h: string; p?: string[]; ul?: string[] }[]
}

const DOCS: Record<'privacy' | 'terms' | 'disclaimer', LegalDoc> = {
  privacy: {
    path: '/privacy',
    title: 'Privacy Policy',
    metaTitle: 'Privacy Policy | Dormer Calculator',
    description: 'How Dormer Calculator collects, uses and protects information, including cookies and advertising.',
    updated: 'March 2025',
    sections: [
      { h: 'Overview', p: ['This policy explains what information www.dormercalculator.com ("we", "the site") collects and how it is used. We keep data collection to a minimum.'] },
      { h: 'Calculator inputs', p: ['Measurements you enter into the calculator are processed in your browser. We do not store them on our servers. If you use "Share Results", the values are placed in a link that you choose to send.'] },
      { h: 'Cookies and advertising', p: ['We may display ads from Google AdSense and similar partners. These partners may use cookies to serve ads based on your visits to this and other sites. You can manage personalized advertising at Google Ads Settings, or opt out at aboutads.info.'] },
      { h: 'Analytics', p: ['We may use privacy-conscious analytics to understand which pages are useful. Data is aggregated and does not identify you personally.'] },
      { h: 'Your rights', ul: ['Withdraw consent for personalized ads through your browser or ad settings.', 'Clear cookies and site data at any time in your browser settings.'] },
      { h: 'Children', p: ['The site is not directed at children under 13 and we do not knowingly collect their data.'] },
      { h: 'Changes', p: ['We may update this policy. The date at the top shows the latest revision.'] },
    ],
  },
  terms: {
    path: '/terms',
    title: 'Terms of Use',
    metaTitle: 'Terms of Use | Dormer Calculator',
    description: 'The terms that apply when you use the Dormer Calculator website and tools.',
    updated: 'March 2025',
    sections: [
      { h: 'Acceptance', p: ['By using www.dormercalculator.com you agree to these terms. If you do not agree, please do not use the site.'] },
      { h: 'Use of the site', p: ['The calculator and articles are provided for personal and professional planning. You agree not to misuse the site, attempt to disrupt it, or scrape it at a rate that harms performance.'] },
      { h: 'No professional advice', p: ['Content is general information. It is not engineering, architectural, legal or construction advice. See the disclaimer for details.'] },
      { h: 'Intellectual property', p: ['Site text, design and code are owned by Dormer Calculator. You may quote short excerpts with a link back to the source. You may not republish whole articles without permission.'] },
      { h: 'Third-party links and ads', p: ['The site may link to or display third-party content. We are not responsible for the content or practices of third parties.'] },
      { h: 'Limitation of liability', p: ['To the fullest extent allowed by law, we are not liable for losses arising from use of, or reliance on, the site or its estimates.'] },
      { h: 'Changes', p: ['We may change the site or these terms at any time. Continued use means you accept the updated terms.'] },
    ],
  },
  disclaimer: {
    path: '/disclaimer',
    title: 'Disclaimer',
    metaTitle: 'Disclaimer | Dormer Calculator',
    description: 'Dormer Calculator results are planning estimates, not quotes or professional advice. Read the full disclaimer.',
    updated: 'March 2025',
    sections: [
      { h: 'Estimates only', p: ['Results from the dormer calculator are approximate planning figures based on average installed prices and simplified geometry. They are not quotes, bids, structural designs or guarantees.'] },
      { h: 'Actual costs vary', ul: ['Local labor and material prices.', 'Site access, existing roof condition and hidden defects.', 'Design complexity, engineering requirements and finishes.', 'Permit fees and inspection requirements in your area.'] },
      { h: 'Not professional advice', p: ['Always consult a licensed architect, structural engineer and building contractor before starting work. Structural changes to a roof must be designed and approved by qualified professionals.'] },
      { h: 'Regulations', p: ['Planning, zoning and building rules differ by location and change over time. Confirm requirements with your local authority before you build.'] },
      { h: 'Accuracy of content', p: ['We work to keep content accurate and current but do not warrant that it is complete or error free. Use of the site is at your own risk.'] },
      { h: 'Advertising', p: ['The site may show advertisements. Ads are not endorsements and do not influence calculator results.'] },
    ],
  },
}

function Legal({ doc }: { doc: keyof typeof DOCS }) {
  const d = DOCS[doc]
  return (
    <>
      <Seo title={d.metaTitle} description={d.description} path={d.path} />
      <div className="border-b border-steel-200 bg-white">
        <div className="container-page py-10">
          <p className="eyebrow">Legal</p>
          <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl">{d.title}</h1>
          <p className="mt-2 text-sm text-steel-500">Last updated: {d.updated}</p>
        </div>
      </div>
      <div className="container-page py-10">
        <div className="legal-body mx-auto max-w-3xl text-lg">
          {d.sections.map(s => (
            <section key={s.h}>
              <h2>{s.h}</h2>
              {s.p?.map(t => <p key={t}>{t}</p>)}
              {s.ul && <ul>{s.ul.map(i => <li key={i}>{i}</li>)}</ul>}
            </section>
          ))}
        </div>
      </div>
    </>
  )
}

export default Legal
